<template>
  <div>
    <v-menu
      v-model="showNotificationsMenu"
      location="bottom end"
      offset="8"
      :close-on-content-click="false"
    >
      <template #activator="{ props: menuProps }">
        <v-badge
          v-if="notificationsList.length"
          color="red"
          :content="notificationsList.length"
          bordered
          offset-y="10"
          offset-x="6"
        >
          <v-btn
            id="notification"
            v-bind="menuProps"
            color="primary"
            icon="mdi-bell-outline"
            variant="text"
            class="user"
          />
        </v-badge>
        <v-btn
          v-else
          id="notification"
          v-bind="menuProps"
          color="primary"
          icon="mdi-bell-outline"
          variant="text"
          class="user"
        />
      </template>

      <NotificationList
        :on-mark-as-read="handleMarkAsRead"
        @close="showNotificationsMenu = false"
        @view="handleViewNotificationFromList"
      />
    </v-menu>

    <NotificationModal
      v-model="showModal"
      :message="currentNotification?.cleaned_message || ''"
      :date="currentNotification?.date || ''"
      :notification-id="currentNotification?.id"
      @close="handleCloseNotification"
      @view="handleViewNotification"
    />

    <NotificationFullView
      v-model="showFullView"
      :message="fullViewMessage"
      :date="fullViewDate"
      :notification-id="fullViewNotificationId"
      :on-mark-as-read="handleMarkAsRead"
    />
  </div>
</template>

<script setup>
import { onMounted, ref, watch, nextTick } from 'vue'
import { api } from '@/api/Api'
import { DEFAULT_ID_PROJECT } from '@/constants'
import NotificationModal from '@/components/Notifications/NotificationModal.vue'
import NotificationFullView from '@/components/Notifications/NotificationFullView.vue'
import NotificationList from '@/components/Notifications/NotificationList.vue'

const notificationsList = ref([])
const isLoading = ref(false)
const shownNotificationIds = ref(new Set())
const currentNotification = ref(null)
const showModal = ref(false)
const showFullView = ref(false)
const showNotificationsMenu = ref(false)
const fullViewMessage = ref('')
const fullViewDate = ref('')
const fullViewNotificationId = ref(null)

async function getNewNotifications() {
  isLoading.value = true
  try {
    const data = await api.notifications().getNewNotifications()
    const newItems = data?.data?.items || []

    const newNotifications = newItems.filter((item) => !shownNotificationIds.value.has(item.id))

    notificationsList.value = newItems

    if (newNotifications.length > 0 && !showModal.value && !showFullView.value) {
      await nextTick()
      await showNextNotification(newNotifications)
    }

    setTimeout(() => {
      getNewNotifications()
    }, 60000)
  } catch (err) {
    console.log(err)
  } finally {
    isLoading.value = false
  }
}

async function showNextNotification(notifications) {
  if (notifications.length === 0) return

  const notification = notifications[0]
  currentNotification.value = notification
  showModal.value = true
}

async function handleCloseNotification(id) {
  if (id) {
    await changeNotifyStatus(id)
    shownNotificationIds.value.add(id)
  }

  showModal.value = false
  currentNotification.value = null

  await nextTick()

  const unshownNotifications = notificationsList.value.filter(
    (item) => !shownNotificationIds.value.has(item.id),
  )

  if (unshownNotifications.length > 0) {
    await showNextNotification(unshownNotifications)
  }
}

function handleViewNotification(notification) {
  fullViewMessage.value = notification.message
  fullViewDate.value = notification.date
  fullViewNotificationId.value = notification.id
  showFullView.value = true

  if (notification.id) {
    shownNotificationIds.value.add(notification.id)
  }

  showModal.value = false
  currentNotification.value = null
}

function handleViewNotificationFromList(notification) {
  showNotificationsMenu.value = false
  handleViewNotification(notification)
}

async function changeNotifyStatus(id) {
  const data = new FormData()
  data.append('message[]', id)
  data.append('project', DEFAULT_ID_PROJECT)
  await api.notifications().readNotify(data)
}

async function handleMarkAsRead(id) {
  if (!id) {
    throw new Error('ID уведомления не указан')
  }

  await changeNotifyStatus(id)
  shownNotificationIds.value.add(id)
  await getNewNotifications()
}

async function handleCloseFullView() {
  showFullView.value = false

  await nextTick()

  const unshownNotifications = notificationsList.value.filter(
    (item) => !shownNotificationIds.value.has(item.id),
  )

  if (unshownNotifications.length > 0) {
    await showNextNotification(unshownNotifications)
  }
}

watch(
  () => notificationsList.value,
  (newList, oldList) => {
    if (
      (!oldList || oldList.length === 0) &&
      newList.length > 0 &&
      !showModal.value &&
      !showFullView.value
    ) {
      const unshownNotifications = newList.filter(
        (item) => !shownNotificationIds.value.has(item.id),
      )
      if (unshownNotifications.length > 0) {
        showNextNotification(unshownNotifications)
      }
    }
  },
  { deep: true },
)

watch(
  () => showFullView.value,
  (newVal, oldVal) => {
    if (oldVal && !newVal) {
      handleCloseFullView()
    }
  },
)

onMounted(getNewNotifications)
</script>
